import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import { validateRequired, getPasswordValidation } from '../../utils/validators';
import './EmployeePage.css';

const Account = () => {
  const { user } = useAuth();

  const [pwData, setPwData] = useState({ current: '', newPw: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    const errs = {};
    if (!validateRequired(pwData.current)) errs.current = 'Current password is required';
    if (!validateRequired(pwData.newPw)) {
      errs.newPw = 'New password is required';
    } else {
      const v = getPasswordValidation(pwData.newPw);
      if (!v.isValid) errs.newPw = v.errors[0];
    }
    if (!validateRequired(pwData.confirm)) {
      errs.confirm = 'Please confirm your new password';
    } else if (pwData.newPw !== pwData.confirm) {
      errs.confirm = 'Passwords do not match';
    }

    setPwErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setPwSubmitting(true);
    setPwError('');
    setPwSuccess('');

    try {
      await api.post('/auth/change-password/', {
        current_password: pwData.current,
        new_password: pwData.newPw,
      });
      setPwSuccess('Password updated successfully.');
      setPwData({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      const data = err.response?.data;
      if (Array.isArray(data?.error)) {
        setPwError(data.error.join(' '));
      } else if (data?.error) {
        setPwError(data.error);
      } else {
        setPwError('Failed to update password. Please try again.');
      }
    } finally {
      setPwSubmitting(false);
    }
  };

  return (
    <div className="employee-page">
      <div className="employee-page__header">
        <p className="employee-page__eyebrow">Employee Portal</p>
        <h1 className="employee-page__title">Account</h1>
      </div>

      <div style={{ maxWidth: '480px' }}>
        {user?.email && (
          <p style={{ marginBottom: '1.5rem', color: 'var(--emp-color-muted, #6b6459)', fontSize: '0.875rem' }}>
            Signed in as <strong>{user.email}</strong>
          </p>
        )}

        <form onSubmit={handlePasswordChange}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Change Password</h2>

            {pwSuccess && (
              <div style={{ marginBottom: '1rem' }}>
                <Alert type="success">{pwSuccess}</Alert>
              </div>
            )}
            {pwError && (
              <div style={{ marginBottom: '1rem' }}>
                <Alert type="error">{pwError}</Alert>
              </div>
            )}

            <Input
              type="password"
              name="current_password"
              label="Current Password"
              value={pwData.current}
              onChange={(e) => {
                setPwData(prev => ({ ...prev, current: e.target.value }));
                if (pwErrors.current) setPwErrors(prev => { const p = { ...prev }; delete p.current; return p; });
                if (pwError) setPwError('');
                if (pwSuccess) setPwSuccess('');
              }}
              error={pwErrors.current}
              autoComplete="current-password"
              required
            />

            <Input
              type="password"
              name="new_password"
              label="New Password"
              value={pwData.newPw}
              onChange={(e) => {
                setPwData(prev => ({ ...prev, newPw: e.target.value }));
                if (pwErrors.newPw) setPwErrors(prev => { const p = { ...prev }; delete p.newPw; return p; });
                if (pwError) setPwError('');
                if (pwSuccess) setPwSuccess('');
              }}
              error={pwErrors.newPw}
              autoComplete="new-password"
              required
            />

            <Input
              type="password"
              name="confirm_password"
              label="Confirm New Password"
              value={pwData.confirm}
              onChange={(e) => {
                setPwData(prev => ({ ...prev, confirm: e.target.value }));
                if (pwErrors.confirm) setPwErrors(prev => { const p = { ...prev }; delete p.confirm; return p; });
                if (pwError) setPwError('');
                if (pwSuccess) setPwSuccess('');
              }}
              error={pwErrors.confirm}
              autoComplete="new-password"
              required
            />
          </div>

          <Button type="submit" variant="primary" disabled={pwSubmitting}>
            {pwSubmitting ? 'Updating...' : 'Update Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Account;
